import React, { useContext, useEffect, useRef, useState, type ReactElement } from 'react';
import Logo from '@assets/images/logo-min.svg';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { breakpoints, Button, Typography, useMediaQuery } from 'gd-design-library';
import classnames from 'classnames/bind';
import Cookies from 'js-cookie';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { CartContext } from '@utils/CartContext';
import { publishAgentCartSync } from './utils/agentCartSync';
import { useClientSideFunctions } from './utils/useClientSideFunctions';
import styles from './cxAgentStudioMessenger.module.scss';
import { useRouter } from 'next/router';
import { handleCartUpdate } from './utils/handleCartUpdate';
import { TENANT_CONFIG } from './constants';
import { cartLineItemsToAgentPayload } from './utils/cartLineItemsToAgentPayload';
import { applyChatMessengerRichContentConfig } from './utils/applyChatMessengerRichContentConfig';
import { setupProductCarouselSalePriceObserver } from './utils/productCarouselSalePriceDom';
import { cleanupUjetHandoff, initUjetClient, type UjetUserRef, type UjetUserRefValue } from './utils/ujetHandoff';
import { UserContext } from '@utils/UserContext';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import { setupNoRecommendationsSuppressor } from './utils/noRecommendationsSuppressor';
import { setupBestMatchSubtitleObserver } from './utils/bestMatchSubtitleDom';

const cx = classnames.bind(styles);
const CN = 'cx-agent-studio-messenger';
const chatBubbleClassName = `${CN}__chat-bubble`;
const headerBannerClassName = `${CN}__header-banner`;
const poweredByClassName = `${CN}__powered-by`;
const expandChatButtonClassName = `${CN}__expand-chat-button`;
const resetSessionButtonClassName = `${CN}__reset-session-button`;

const getVolcanoPageUrl = (): string => window?.location?.href ?? '';

export const CXAgentStudioMessenger = (): ReactElement => {
  const [ isExpanded, setIsExpanded ] = useState(false);
  const [ isChatOpen, setIsChatOpen ] = useState(false);
  const [ isClient, setIsClient ] = useState(false);
  const chatMessengerRef = useRef<React.ComponentRef<'chat-messenger'> | null>(null);
  const variablesStoreRef = useRef<Record<string, unknown>>({});
  const { cart, setCart } = useContext(CartContext);
  const { user } = useContext(UserContext);
  const userRef = useRef<UjetUserRef['current']>(null);
  const router = useRouter();
  const setMergedVariables = (vars: Record<string, unknown>): void => {
    Object.assign(variablesStoreRef.current, vars);
    chatMessengerRef.current?.setVariables?.(variablesStoreRef.current);
  };
  const registerClientSideFunctions = useClientSideFunctions(userRef);
  const isMobileOrTablet = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const selectedTenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || '{}')?.key;
  const tenantMessengerConfig = TENANT_CONFIG[selectedTenantKey as keyof typeof TENANT_CONFIG];
  const chatMessengerIntent =
    tenantMessengerConfig && 'intent' in tenantMessengerConfig ? tenantMessengerConfig.intent : undefined;
  const isSidebarExperience = isFeatureEnabled(FEATURES.GECX_SIDEBAR);
  const isDesktopSidebar = isSidebarExperience && !isMobileOrTablet;

  useEffect(() => { setIsClient(true); }, []);

  /**
   * Keep the userRef in sync with the latest authenticated user so UJET handoff
   * authentication can read it without re-initializing the client.
   */
  useEffect(() => {
    if (!user) {
      userRef.current = null;

      return;
    }
    const { firstName, lastName, email } = user;
    const name = [ firstName, lastName ].filter(Boolean).join(' ').trim();
    if (!email) {
      userRef.current = null;

      return;
    }
    const identity: UjetUserRefValue = { name: name || email, email };
    userRef.current = identity;
  }, [ user ]);

  /**
   * Initialize the chat messenger and register the context.
   */
  useEffect(() => {
    const handleChatMessengerLoaded = (): void => {
      if (window.chatSdk) {
        window.chatSdk.registerContext(
          window.chatSdk.prebuilts.ces.createContext({
            deploymentName: process.env.NEXT_PUBLIC_CX_AGENT_STUDIO_DEPLOYMENT_NAMES[selectedTenantKey],
            tokenBroker: {
              enableTokenBroker: true,
              enableRecaptcha: false,
            },
          }),
        );
      }

      applyChatMessengerRichContentConfig();

      registerClientSideFunctions(chatMessengerRef.current);
      if (chatMessengerRef.current) {
        void initUjetClient(chatMessengerRef.current, userRef);
      }

      const loadedVariables: Record<string, string> = {
        volcano_page_url: getVolcanoPageUrl(),
        volcano_channel: 'web',
      };
      const accessToken = Cookies.get('ct_access_token');
      if (accessToken) loadedVariables.volcano_access_token = accessToken;
      setMergedVariables(loadedVariables);
    };

    window.addEventListener('chat-messenger-loaded', handleChatMessengerLoaded);

    return (): void => {
      window.removeEventListener('chat-messenger-loaded', handleChatMessengerLoaded);
      cleanupUjetHandoff();
    };
  }, [ selectedTenantKey, router ]);

  /**
   * Handle the chat messenger open changed event.
   */
  useEffect(() => {
    const handleChatMessengerOpenChanged = (event: CustomEvent<{ isOpen?: boolean }>): void => {
      setIsChatOpen(event.detail?.isOpen ?? false);

      if (!chatMessengerRef.current) return;

      if (event.detail?.isOpen && !chatMessengerRef.current.presenter.getSessionId()) {
        if (tenantMessengerConfig?.startNewSessionOnOpenWhenNoSession !== false) {
          chatMessengerRef.current.startNewSession();
        }
        setMergedVariables({
          volcano_page_url: getVolcanoPageUrl(),
          volcano_channel: 'web',
        });
      }
    };

    window.addEventListener('chat-messenger-open-changed', handleChatMessengerOpenChanged);

    return (): void => {
      window.removeEventListener('chat-messenger-open-changed', handleChatMessengerOpenChanged);
    };
  }, [ tenantMessengerConfig?.startNewSessionOnOpenWhenNoSession ]);

  /**
   * Sync volcano_page_url, volcano_channel, and access token when the session id is set.
   */
  useEffect(() => {
    const syncVolcanoVariablesOnSessionIdSet = (): void => {
      const accessToken = Cookies.get('ct_access_token');
      const messenger = chatMessengerRef.current;
      if (!messenger) return;
      const variables: Record<string, string> = {
        volcano_page_url: getVolcanoPageUrl(),
        volcano_channel: 'web',
      };
      if (accessToken) variables.volcano_access_token = accessToken;
      setMergedVariables(variables);
    };

    window.addEventListener('chat-messenger-session-id-set', syncVolcanoVariablesOnSessionIdSet);

    return (): void => {
      window.removeEventListener('chat-messenger-session-id-set', syncVolcanoVariablesOnSessionIdSet);
    };
  }, []);

  /**
   * Sync volcano_page_url when the path changes.
   */
  useEffect(() => {
    if (!isClient) return;
    setMergedVariables({ volcano_page_url: getVolcanoPageUrl() });
  }, [ isClient, router.asPath ]);

  /**
   * Store the session id and expires at in the session storage when the access token is resolved.
   * This prevents the session from being lost when the page is refreshed.
   */
  useEffect(() => {
    const handleChatMessengerAccessTokenResolved = (): void => {
      const ctx = window.chatSdk?.getContext();

      if (ctx?.state?.sessionId && !sessionStorage.getItem('chat-messenger-session-id')) {
        sessionStorage.setItem('chat-messenger-session-id', ctx.state.sessionId);
        const expiresAt = ctx.state.sessionExpiresAt
          || new Date(Date.now() + 30 * 60 * 1000).toISOString(); // 30 minutes from now by default
        sessionStorage.setItem('chat-messenger-session-id-expires-at', expiresAt);
      }
    };

    window.addEventListener('chat-messenger-access-token-resolved', handleChatMessengerAccessTokenResolved);

    return (): void => {
      window.removeEventListener('chat-messenger-access-token-resolved', handleChatMessengerAccessTokenResolved);
    };
  }, []);

  /**
   * Publish the cart to the agent when the cart is updated.
   */
  useEffect(() => {
    if (!isClient) return;

    const items = cartLineItemsToAgentPayload(cart);
    publishAgentCartSync(items);
    setMergedVariables({ cart: JSON.stringify(items) });
  }, [ isClient, cart ]);

  /**
   * Handle the chat messenger cart update event.
   */
  useEffect(() => {
    if (!isClient || !chatMessengerRef.current) return;

    const onAddToCart = (event: Event): void => { handleCartUpdate(event, setCart); };
    chatMessengerRef.current.addEventListener('chat-messenger-add-to-cart', onAddToCart);

    return (): void => {
      chatMessengerRef.current?.removeEventListener('chat-messenger-add-to-cart', onAddToCart);
    };
  }, [ isClient, setCart ]);

  /**
   * Import the product quantity toggle and order history widget components.
   */
  useEffect(() => {
    Promise.all([ import('./customElements/productQuantityToggle').then(({ ProductQuantityToggle }) => {
      if (!customElements.get('product-quantity-toggle')) {
        customElements.define('product-quantity-toggle', ProductQuantityToggle);
      }
    }),
    import('./customElements/orderHistoryWidget').then(({ OrderHistoryWidget }) => {
      if (!customElements.get('order-history-widget')) {
        customElements.define('order-history-widget', OrderHistoryWidget);
      }
    }),
    import('./customElements/showProductsOnPageButton').then(({ ShowProductsOnPageButton }) => {
      if (!customElements.get('show-products-on-page-button')) {
        customElements.define('show-products-on-page-button', ShowProductsOnPageButton);
      }
    }),
    import('./customElements/ujetAgentMessage').then(({ UjetAgentMessage }) => {
      if (!customElements.get('ujet-agent-message')) {
        customElements.define('ujet-agent-message', UjetAgentMessage);
      }
    }),
    import('./customElements/ujetSystemMessage').then(({ UjetSystemMessage }) => {
      if (!customElements.get('ujet-system-message')) {
        customElements.define('ujet-system-message', UjetSystemMessage);
      }
    }) ]);
  }, []);

  useEffect(() => {
    import('./customElements/showProductsOnPageButton').then(({ setShowProductsOnPageNavigate }) => {
      setShowProductsOnPageNavigate(router.push);
    });
    import('./customElements/orderHistoryWidget').then(({ setOrderHistoryFullHistoryNavigate }) => {
      setOrderHistoryFullHistoryNavigate(router.push);
    });
  }, [ router ]);

  useEffect(() => setupProductCarouselSalePriceObserver(), []);

  useEffect(() => setupNoRecommendationsSuppressor(), []);

  useEffect(() => setupBestMatchSubtitleObserver(), []);

  useEffect(() => {
    const root = document.documentElement;
    const { body } = document;
    if (!isClient || !isDesktopSidebar || !isChatOpen) {
      body.classList.remove('cx-agent-sidebar-layout');
      root.style.removeProperty('--cx-agent-sidebar-reserve');

      return;
    }

    body.classList.add('cx-agent-sidebar-layout');
    root.style.setProperty('--cx-agent-sidebar-reserve', '420px');

    return (): void => {
      body.classList.remove('cx-agent-sidebar-layout');
      root.style.removeProperty('--cx-agent-sidebar-reserve');
    };
  }, [ isClient, isDesktopSidebar, isChatOpen ]);

  useEffect((): void => {
    if (!isSidebarExperience) {
      setIsChatOpen(false);
    } else {
      setIsExpanded(false);
    }
  }, [ isSidebarExperience ]);

  return isClient ? (
    <chat-messenger
      url-allowlist='*'
      language-code='en'
      max-query-length='-1'
      { ...(chatMessengerIntent ? { intent: chatMessengerIntent } : {}) }
      top-anchoring={ isDesktopSidebar || undefined }
      className={ cx(CN, {
        [`${CN}--expanded`]: isExpanded && !isSidebarExperience,
        [`${CN}--sidebar`]: isDesktopSidebar,
      }) }
      ref={ chatMessengerRef }
    >
      <chat-messenger-chat-bubble
        chat-title={ tenantMessengerConfig?.['chat-title'] }
        chat-title-icon={ Logo.src }
        enable-file-upload
        enable-audio-input
        close-icon='close'
        align-bottom-right={ isDesktopSidebar || undefined }
        className={ cx(chatBubbleClassName) }
      >
        <chat-reset-session-button
          slot='titlebar-actions'
          title-text='Start new chat'
          className={ cx(resetSessionButtonClassName) }
        />
        {!isMobileOrTablet && !isSidebarExperience && (
          <Button
            isIcon
            variant='text'
            slot='titlebar-actions'
            title='Expand chat'
            className={ cx(expandChatButtonClassName) }
            onClick={ (): void => setIsExpanded(!isExpanded) }
          >
            {isExpanded ? <CloseFullscreenIcon fontSize='small' /> : <OpenInFullIcon fontSize='small' />}
          </Button>
        )}
        <div id='header-banner' slot='messages-header'>
          <Typography variant='caption' align='center' className={ cx(poweredByClassName) }>Powered by Agent Studio</Typography>
          {tenantMessengerConfig?.['messages-header-src'] ? (
            <img
              alt='header-banner'
              src={ tenantMessengerConfig['messages-header-src'] }
              className={ cx(headerBannerClassName) }
            />
          ) : null}
          {tenantMessengerConfig?.['messages-header-intro'] ? (
            <div style={ { margin: '10px 36px 18px 36px', lineHeight: 'normal' } }>
              {tenantMessengerConfig['messages-header-intro']}
            </div>
          ) : null}
        </div>
      </chat-messenger-chat-bubble>
    </chat-messenger>
  ) : null;
};
